import { Injectable } from '@angular/core';
import { StatsRHCachedData } from '@shared/interfaces/statsRH.interfaces';
import { newUserConfiguration } from '@shared/interfaces/userConfiguration.interfaces';
import { FormationsGroups } from '@shared/models/formationsGroups.model';
import { UserConfiguration } from '@shared/models/userConfiguration.model';
import { BehaviorSubject } from 'rxjs';
import { Subject } from 'rxjs/internal/Subject';

@Injectable({ providedIn: 'root' })
export class HrsuiteService {
  public requestCalendarsFromHospitalSubject: Subject<void> = new Subject();

  public adjustPlanningSubject: Subject<void> = new Subject();

  public profilesUpdateSubject: Subject<any> = new Subject<string>();

  public calendarsUpdateSubject: Subject<any> = new Subject<string>();

  public toggleEventsSubject: Subject<boolean> = new Subject<boolean>();

  public toggleNightSubject: Subject<boolean> = new Subject<boolean>();

  public darkModeSubject: Subject<boolean> = new Subject<boolean>();

  public reloadActiveRequestsCounterSubject: Subject<void> = new Subject<void>();

  public isSurgeonOpeningsRoomsSubject: Subject<boolean> = new Subject<boolean>();

  public isChangeSurgeonRoomsSubject: Subject<boolean> = new Subject<boolean>();

  public saveRoomsSubject: Subject<boolean> = new Subject<boolean>();

  public isSavingSurgeonOpenings: boolean = false;

  public isChangeSurgeonRooms: boolean = false;

  public refreshRoomsSubject: Subject<boolean> = new Subject<boolean>();

  public requestDatesSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  public isSurgeonOpeningsRoomDetailsSubject: Subject<boolean> = new Subject<boolean>();

  public addInterventionSubject: Subject<boolean> = new Subject<boolean>();

  public closeRoomSubject: Subject<boolean> = new Subject<boolean>();

  public isClosingRoom: boolean = false;

  public isAnesthNeedsPageSubject: Subject<boolean> = new Subject<boolean>();

  public isChangeAnesthNeedsSubject: Subject<boolean> = new Subject<boolean>();

  public saveAnesthNeedsSubject: Subject<boolean> = new Subject<boolean>();

  public isSavingAnesthNeeds: boolean = false;

  public refreshAnesthNeedsSubject: Subject<boolean> = new Subject<boolean>();

  public isAnesthResponsibleRoomDetailsSubject: Subject<boolean> = new Subject<boolean>();

  public isPerInterventionsPageSubject: Subject<boolean> = new Subject<boolean>();

  // Subject for strategic/operational mode in Stats RH
  public strategicModeSubject: Subject<boolean> = new Subject<boolean>();

  public statsRHUserConfigSubject: Subject<newUserConfiguration | UserConfiguration> = new Subject<
    newUserConfiguration | UserConfiguration
  >();

  public statsRHLoadingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public statsRHLoading$ = this.statsRHLoadingSubject.asObservable();

  public changeSateSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public selectedPopulationSubject: BehaviorSubject<any> = new BehaviorSubject<any>({});

  public selectedFormationGroupSubject: BehaviorSubject<FormationsGroups> = new BehaviorSubject<FormationsGroups>({});

  public loadStatsRhDataSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public exportEquityTableSubject: Subject<void> = new Subject<void>();

  public anesthesistesMessages: Subject<any> = new Subject<any>();

  public campaignPosition: Subject<string> = new Subject<string>();

  public listePositions: Subject<string> = new Subject<string>();

  public demandePositions: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>(undefined);

  public activeRequestsCounters: number[] = [];

  private updateNumberOfCRSubject = new Subject<string[] | undefined>();

  public updateNumberOfCR$ = this.updateNumberOfCRSubject.asObservable();

  private readonly statsRHCache = new Map<string, StatsRHCachedData>();

  get(key: string): StatsRHCachedData | null {
    return this.statsRHCache.get(key) ?? null;
  }

  has(key: string): boolean {
    return this.statsRHCache.has(key);
  }

  set(payload: StatsRHCachedData): void {
    this.statsRHCache.set(payload.key, payload);
  }

  getMostRecent(): StatsRHCachedData | null {
    if (this.statsRHCache.size === 0) {
      return null;
    }

    const mostRecent = Array.from(this.statsRHCache.values()).reduce((latest, current) =>
      current.createdAt > latest.createdAt ? current : latest
    );

    return mostRecent;
  }

  getMostRecentForHospitals(hospitals?: string): StatsRHCachedData | null {
    if (this.statsRHCache.size === 0 || !hospitals) {
      return null;
    }

    const filteredEntries = Array.from(this.statsRHCache.values()).filter((entry) => entry.hospitals === hospitals);

    if (filteredEntries.length === 0) {
      return null;
    }

    const mostRecent = filteredEntries.reduce((latest, current) =>
      current.createdAt > latest.createdAt ? current : latest
    );
    return mostRecent;
  }

  clearCache(): void {
    this.statsRHCache.clear();
  }

  updateNumberOfCR(args?: string[]): void {
    this.updateNumberOfCRSubject.next(args);
  }

  clear(): void {
    this.clearCache();
    this.requestCalendarsFromHospitalSubject.complete();
    this.requestCalendarsFromHospitalSubject = new Subject<void>();
    this.adjustPlanningSubject.complete();
    this.adjustPlanningSubject = new Subject<void>();

    this.profilesUpdateSubject.complete();
    this.profilesUpdateSubject = new Subject<any>();

    this.calendarsUpdateSubject.complete();
    this.calendarsUpdateSubject = new Subject<any>();

    this.toggleEventsSubject.complete();
    this.toggleEventsSubject = new Subject<boolean>();

    this.toggleNightSubject.complete();
    this.toggleNightSubject = new Subject<boolean>();

    this.darkModeSubject.complete();
    this.darkModeSubject = new Subject<boolean>();

    this.reloadActiveRequestsCounterSubject.complete();
    this.reloadActiveRequestsCounterSubject = new Subject<void>();

    this.isSurgeonOpeningsRoomsSubject.complete();
    this.isSurgeonOpeningsRoomsSubject = new Subject<boolean>();

    this.isChangeSurgeonRoomsSubject.complete();
    this.isChangeSurgeonRoomsSubject = new Subject<boolean>();

    this.saveRoomsSubject.complete();
    this.saveRoomsSubject = new Subject<boolean>();

    this.refreshRoomsSubject.complete();
    this.refreshRoomsSubject = new Subject<boolean>();

    this.requestDatesSubject.complete();
    this.requestDatesSubject = new BehaviorSubject<any>(null);

    this.isSurgeonOpeningsRoomDetailsSubject.complete();
    this.isSurgeonOpeningsRoomDetailsSubject = new Subject<boolean>();

    this.addInterventionSubject.complete();
    this.addInterventionSubject = new Subject<boolean>();

    this.closeRoomSubject.complete();
    this.closeRoomSubject = new Subject<boolean>();

    this.isAnesthNeedsPageSubject.complete();
    this.isAnesthNeedsPageSubject = new Subject<boolean>();

    this.isChangeAnesthNeedsSubject.complete();
    this.isChangeAnesthNeedsSubject = new Subject<boolean>();

    this.saveAnesthNeedsSubject.complete();
    this.saveAnesthNeedsSubject = new Subject<boolean>();

    this.refreshAnesthNeedsSubject.complete();
    this.refreshAnesthNeedsSubject = new Subject<boolean>();

    this.isAnesthResponsibleRoomDetailsSubject.complete();
    this.isAnesthResponsibleRoomDetailsSubject = new Subject<boolean>();

    this.isPerInterventionsPageSubject.complete();
    this.isPerInterventionsPageSubject = new Subject<boolean>();

    this.strategicModeSubject.complete();
    this.strategicModeSubject = new Subject<boolean>();

    this.statsRHUserConfigSubject.complete();
    this.statsRHUserConfigSubject = new Subject<newUserConfiguration | UserConfiguration>();

    this.statsRHLoadingSubject.complete();
    this.statsRHLoadingSubject = new BehaviorSubject<boolean>(false);

    this.changeSateSubject.complete();
    this.changeSateSubject = new BehaviorSubject<boolean>(false);

    this.selectedPopulationSubject.complete();
    this.selectedPopulationSubject = new BehaviorSubject<any>({});

    this.selectedFormationGroupSubject.complete();
    this.selectedFormationGroupSubject = new BehaviorSubject<FormationsGroups>({});

    // this.selectedPopulationSubject2.complete();
    // this.selectedPopulationSubject2 = new BehaviorSubject<any>({});

    this.loadStatsRhDataSubject.complete();
    this.loadStatsRhDataSubject = new BehaviorSubject<boolean>(false);

    this.exportEquityTableSubject.complete();
    this.exportEquityTableSubject = new Subject<void>();

    this.anesthesistesMessages.complete();
    this.anesthesistesMessages = new Subject<any>();

    this.campaignPosition.complete();
    this.campaignPosition = new Subject<string>();

    this.listePositions.complete();
    this.listePositions = new Subject<string>();

    this.demandePositions.complete();
    this.demandePositions = new BehaviorSubject<string | undefined>(undefined);

    this.updateNumberOfCRSubject.complete();
    this.updateNumberOfCRSubject = new Subject<string[] | undefined>();

    // Reset simple booleans
    this.isSavingSurgeonOpenings = false;
    this.isChangeSurgeonRooms = false;
    this.isClosingRoom = false;
    this.isSavingAnesthNeeds = false;

    this.activeRequestsCounters = [];
  }
}
